import { AsyncPipe } from "@angular/common";
import {
    Component,
    EventEmitter,
    inject,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { FileUploadComponent , FileBase64 } from "@components/file-upload/file-upload.component";
import { IconButtonComponent } from "@components/icon-button/icon-button.component";
import { InfoComponent } from "@components/info/info.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent } from "@components/list/list.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse, RaskRoller, VirkeskopBlobStorageTyp } from "~app/common/models/constants";
import { IContent } from "~app/common/models/validationModel";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ErrorMediatorService } from "~app/common/services/error-mediator.service";
import { SaveVirkeskopsKontraktDokumentDTO } from "~app/features/virkeskop/dto/dokument/SaveVirkeskopsKontraktDokumentDTO";
import { VirkeskopDokumentInfoDTO } from "~app/features/virkeskop/dto/dokument/VirkeskopDokumentInfoDTO";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { JoinValuesPipe } from "../../../../common/pipes/join-values.pipe";
import { KontraktRitunderlagService } from "../../services/kontrakt-ritunderlag.service";

@Component({
    selector: "rask-ritunderlag-map-dialog",
    styleUrls: ["./ritunderlag-map-dialog.component.scss"],
    templateUrl: "./ritunderlag-map-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        IconButtonComponent,
        FileUploadComponent,
        TranslateDirective,
        InfoComponent,
        ButtonComponent,
        AsyncPipe,
        JoinValuesPipe
    ]
})
export class RitunderlagMapDialogComponent implements OnInit {
    readonly maxFileSize = 1024 * 1024 * 50;
    readonly maxFilenameLength = 50;

    acceptedFiletype = "zip";
    dokumentTyp = VirkeskopBlobStorageTyp.RitunderlagBilaga;
    file: FileBase64;
    fileActionInProgress = false;
    fileContent: string;
    fileExtension: string;
    fileName = "";
    @Output() fileUpload = new EventEmitter();
    fileUploadError = "";
    modal = inject(NgbActiveModal);
    ritunderlag$: Observable<VirkeskopDokumentInfoDTO[]>;

    protected readonly = false;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktRitunderlagService = inject(KontraktRitunderlagService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly validationService = inject(ErrorMediatorService);

    get hasUploadFilePermission() {
        return this.authenticationService.hasAnyRole([
            RaskRoller.role.Kopleda,
            RaskRoller.role.Virkeskopa,
            RaskRoller.role.Kopplanera,
            RaskRoller.role.Skogsvardsuppdragsplanera
        ]);
    }

    get kontraktId(): number {
        return this.kontraktMediatorService.kontrakt.id;
    }

    get uploadButtonInactive() {
        return this.fileActionInProgress || this.fileName.length <= 0;
    }

    ngOnInit(): void {
        this.ritunderlag$ = this.kontraktMediatorService.ritunderlag$;
    }

    async onFileSelected(files: FileBase64[]) {
        const file = files[0];

        this.fileName = file.name;

        this.fileExtension = this.fileName.split(".").pop();

        if (this.acceptedFiletype === this.fileExtension.toLowerCase()) {
            this.fileUploadError = "";
        } else {
            this.fileName = "";
            this.fileUploadError = "Välj fil av typen Zip";
            return;
        }

        if (file) {
            this.file = file;
            this.fileContent = file.content;
            if (file.size > this.maxFileSize) {
                this.fileUploadError = "Filen är för stor. Max 50 MB";
                this.clearFileInput();
            }

            if (file.name.length > this.maxFilenameLength) {
                this.fileUploadError = "Filen har för långt namn. Max 50 tecken.";
                this.clearFileInput();
            }
        }
    }

    uploadDocument() {
        if (this.readonly) {
            return;
        }
        this.fileActionInProgress = true;
        if (this.fileName.length <= 0 || !this.fileContent) {
            return;
        }

        const dokument: SaveVirkeskopsKontraktDokumentDTO = {
            kontraktId: this.kontraktId,
            atgardId: null,
            dokumenttyp: VirkeskopBlobStorageTyp.RitunderlagBilaga,
            files: [this.file]
        };

        this.busyService.addWork(this.kontraktRitunderlagService.uploadDokument(dokument.kontraktId, dokument)).subscribe({
            next: response => {
                this.clearFileInput();
                if (response.validationMessages) {
                    response.validationMessages.forEach(validationMessage => {
                        const msg = {
                            message: validationMessage,
                            messageType: "ValidationMessage"
                        } as IContent;
                        this.validationService.add(msg);
                    });
                } else {
                    this.kontraktMediatorService.addRitunderlag(response);
                }
                this.fileActionInProgress = false;
                this.fileUpload.emit();
                this.kontraktMediatorService.loadRitunderlag(this.kontraktId);
            },
            error: err => {
                this.clearFileInput();
                this.fileUploadError = err;
                this.fileActionInProgress = false;
            }
        });
    }

    protected canDelete() {
        return true;
    }

    protected close() {
        this.modal.dismiss();
    }

    protected deleteDokument(dokumentInfo: VirkeskopDokumentInfoDTO) {
        if (this.readonly) {
            return;
        }
        this.modalDialogService.confirm(new ModalSettings("Ta bort fil", dokumentInfo.filnamn))
            .subscribe(modalResult => {
                if (modalResult === ConfirmDialogResponse.save) {
                    const ritunderlagIndex = this.kontraktMediatorService.ritunderlag.findIndex(b => b.id === dokumentInfo.id);
                    if (dokumentInfo.dokumentnyckel && ritunderlagIndex > -1 && !this.fileActionInProgress) {
                        this.fileActionInProgress = true;

                        this.kontraktRitunderlagService.deleteDokument(dokumentInfo.id)
                            .then(response => {
                                this.kontraktMediatorService.removeRitunderlag(dokumentInfo.id);
                            })
                            .catch(err => {
                                this.fileUploadError = err;
                            })
                            .then(() => {
                                this.fileActionInProgress = false;
                            });
                    }
                }
            });
    }

    private readonly clearFileInput = () => {
        this.file = null;
        this.fileName = "";
        this.fileContent = "";
    };
}
